import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Download, X, Share, MoreVertical } from "lucide-react";
import { useLocation } from "react-router-dom";
import { usePwaInstall } from "../hooks/usePwaInstall";
import { ACADEMY_LOGO } from "../data/assets";

const DISMISS_KEY = "noSoccerInstallDismissedAt";
const DISMISS_DAYS = 7;
const SCROLL_GATE = 300;
const OVERLAY_SELECTOR = '[role="dialog"], [data-overlay], .fixed.inset-0';

const isIos = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent);
const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
const recentlyDismissed = () => Date.now() - Number(localStorage.getItem(DISMISS_KEY) || 0) < DISMISS_DAYS * 86400000;

// Compact single-line "Install" banner: square crest · one-line hint · X. Shows only on phones, only after the
// player scrolls 300px, never while a modal/popup is open, sits above the bottom tab bar, and stays dismissed for 7 days.
export default function InstallBanner() {
  const { canInstall, install, installed } = usePwaInstall();
  const [eligible, setEligible] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [tabBarH, setTabBarH] = useState(0);
  const [showManual, setShowManual] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    if (recentlyDismissed() || isStandalone() || installed || window.location.pathname.startsWith("/print/")) return;
    if (!window.matchMedia("(max-width: 768px)").matches) return;
    if (canInstall) { setShowManual(false); setEligible(true); return; }
    const t = setTimeout(() => { if (!window.__deferredInstallPrompt) { setShowManual(true); setEligible(true); } }, 4000);
    return () => clearTimeout(t);
  }, [canInstall, installed]);

  useEffect(() => {
    const onScroll = () => { if (window.scrollY >= SCROLL_GATE) setScrolled(true); };
    onScroll(); window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const check = () => {
      setOverlayOpen(!!document.querySelector(OVERLAY_SELECTOR));
      setTabBarH(document.querySelector('[data-testid="bottom-tab-bar"]')?.offsetHeight || 0);
    };
    check();
    const mo = new MutationObserver(check); mo.observe(document.body, { childList: true, subtree: true });
    return () => mo.disconnect();
  }, [pathname]);

  const dismiss = () => { localStorage.setItem(DISMISS_KEY, String(Date.now())); setEligible(false); };
  const handleInstall = async () => {
    const res = await install();
    if (res.outcome === "accepted") dismiss();
    else if (res.outcome === "unavailable") setShowManual(true);
  };
  const visible = eligible && scrolled && !overlayOpen;
  const native = canInstall && !showManual;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div data-testid="install-banner" initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 80, opacity: 0 }} transition={{ duration: 0.3, ease: "easeOut" }}
          className="fixed left-3 right-3 z-40 md:hidden no-print" style={{ bottom: `calc(${tabBarH}px + env(safe-area-inset-bottom, 0px) + 0.5rem)` }}>
          <div className="flex items-center gap-2.5 h-12 pl-2 pr-1 bg-[#1A1A1A] border border-white/15 rounded-xl shadow-[0_12px_40px_rgba(0,0,0,0.7)]">
            <img src={ACADEMY_LOGO} alt="" width={28} height={28} className="w-7 h-7 object-contain shrink-0" />
            <button type="button" onClick={native ? handleInstall : undefined} data-testid={native ? "install-banner-install" : "install-manual-hint"}
              className="flex-1 min-w-0 flex items-center gap-1 text-left text-[11.5px] min-[400px]:text-[12px] text-white whitespace-nowrap overflow-hidden min-h-[44px]">
              <span className="font-bold shrink-0">Install Next O</span>
              <span className="text-white/45 shrink-0">—</span>
              {native ? (
                <span className="inline-flex items-center gap-1 text-[#D4AF37] font-semibold truncate"><Download size={13} /> Tap to add to Home Screen</span>
              ) : (
                <span className="inline-flex items-center gap-1 text-white/70 truncate">Tap {isIos() ? <Share size={13} className="text-[#C41E24] shrink-0" /> : <MoreVertical size={13} className="text-[#C41E24] shrink-0" />} <span className="hidden min-[400px]:inline">then</span> <span className="text-white/90 font-semibold">Add to Home Screen</span></span>
              )}
            </button>
            <button type="button" onClick={dismiss} data-testid="install-banner-dismiss" aria-label="Dismiss install banner" className="shrink-0 w-11 h-11 flex items-center justify-center rounded-lg text-white/50 hover:text-white active:bg-white/10"><X size={16} /></button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
